import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SeoRelatedLinks from "../../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "check",
    label: "Trenger restauranten din et digitalt bestillingssystem?",
  },
  {
    id: "definition",
    label: "Hva er et digitalt bestillingssystem for restauranter?",
  },
  {
    id: "types",
    label: "Hvilke typer digital bestilling finnes?",
  },
  {
    id: "expectation",
    label: "Hvorfor digital bestilling blir normalt for gjester",
  },
  {
    id: "direct",
    label: "Direkte bestilling eller tredjepartsplattformer: hva er bedre?",
  },
  {
    id: "qr-ordering",
    label: "QR-kode-bestilling: bestilling direkte fra bordet",
  },
  {
    id: "workflow",
    label: "Hvordan en god digital bestillingsflyt bør se ut",
  },
  {
    id: "payment",
    label: "Digital betaling og kontaktløse arbeidsflyter",
  },
  {
    id: "mistakes",
    label: "Vanlige feil i digitale bestillingssystemer",
  },
  {
    id: "bestplace",
    label: "Hvordan Best Place kan hjelpe med digital bestilling",
  },
  {
    id: "faq",
    label: "FAQ om digitale bestillingssystemer",
  },
];

const checklist = [
  "Gjester spør ofte om henting eller forhåndsbestilling.",
  "Restauranten din mottar bestillinger via telefon, meldinger eller sosiale medier.",
  "Du vil samle bestillinger på en mer strukturert måte.",
  "Du vil koble meny, QR-kode og bestilling tettere sammen.",
  "Du vil ha færre misforståelser rundt navn, priser eller produkter.",
  "Du kan senere tilby henting, takeaway eller bordbestilling digitalt.",
  "Du vil gjøre tillegg som drikke, desserter eller menyer mer synlige.",
  "Du vil at bestillingsflyten skal være enklere på smarttelefon.",
  "Du vil være mindre avhengig av bare tredjepartsplattformer.",
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function getResult(score) {
  if (score <= 3) {
    return {
      title: "Et digitalt bestillingssystem haster kanskje ikke ennå",
      text:
        "Hvis bestillingene dine er sjeldne eller veldig enkle, kan en tydelig digital meny være det bedre første steget.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "En digital bestillingsflyt er verdt å teste",
      text:
        "Restauranten din kan ha nytte av å samle bestillinger tydeligere og koble meny, QR-kode og neste handling bedre sammen.",
      tone: "medium",
    };
  }

  return {
    title: "Digital bestilling kan bli et viktig neste steg",
    text:
      "Flere punkter viser at digital bestilling kan hjelpe restauranten din: mindre kaos, tydeligere bestillingsdata, bedre mobilopplevelse og mer kontroll.",
    tone: "high",
  };
}

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visuell idé">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>

      <div>
        <strong>Idé til illustrasjon:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function DigitalOrderingSystemForRestaurantsNo() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "no");
    document.title =
      "Digitalt bestillingssystem for restauranter | Best Place";
  }, []);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="no"
        langLinks={{
          de: "/de/digitales-bestellsystem-gastronomie",
          en: "/en/digital-ordering-system-for-restaurants",
          no: "/no/digitalt-bestillingssystem-for-restauranter",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Digitalt bestillingssystem for restauranter: fra meny til
              bestilling
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Et digitalt bestillingssystem starter ikke med betaling. Det
                starter der gjesten forstår tilbudet ditt: menyen, QR-koden,
                mobilopplevelsen og et tydelig neste steg.
              </p>

              <p className="seoHero__text">
                For restauranter kan digital bestilling hjelpe med å samle
                bestillinger tydeligere, redusere misforståelser og gjøre tillegg
                som drikke, desserter, menyer, henting eller takeaway mer
                synlige.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="no" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Innhold i artikkelen</h2>

        <nav className="qrArticle__nav" aria-label="Artikkelnavigasjon">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="check" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Trenger restauranten din et digitalt bestillingssystem?</h2>

            <p>
              Trykk på utsagnene som passer for restauranten din. Resultatet er
              bevisst ærlig: ikke alle restauranter trenger digital bestilling
              med en gang, men mange kan ha nytte av en tydeligere flyt fra meny
              til bestilling tidligere enn de tror.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="definition" className="acquaintance">
          <h2>Hva er et digitalt bestillingssystem for restauranter?</h2>

          <p>
            Et digitalt bestillingssystem er en prosess som lar gjester velge mat
            eller drikke via en nettside, digital meny, QR-kode eller
            mobilgrensesnitt og sende en bestilling eller strukturert
            forespørsel.
          </p>

          <p>
            Avhengig av restauranten kan dette bety henting, takeaway, levering,
            forhåndsbestilling eller bordbestilling. Hovedpoenget er ikke bare
            teknologien. Gjesten bør tydelig forstå hva som er tilgjengelig, hva
            det koster og hvordan man bestiller.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Digital meny.</li>
            <li className="text__seo-li">QR-kode-tilgang.</li>
            <li className="text__seo-li">Produktvalg.</li>
            <li className="text__seo-li">Handlekurv eller forespørsel.</li>
            <li className="text__seo-li">
              Henting, levering eller bordbestilling.
            </li>
            <li className="text__seo-li">Valgfri digital betaling.</li>
            <li className="text__seo-li">
              Strukturerte bestillingsdata for restauranten.
            </li>
          </ul>

          <h3>Bestilling bør starte med en tydelig meny</h3>

          <p>
            Hvis menyen er forvirrende, blir bestillingsprosessen også
            forvirrende. Et godt digitalt bestillingssystem trenger et ryddig
            grunnlag: produkter, priser, tilgjengelighet, beskrivelser og en
            enkel mobilflyt.
          </p>
        </section>

        <section id="types" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvilke typer digital bestilling finnes?</h2>

            <p>
              Ikke alle restauranter trenger det samme bestillingssystemet med en
              gang. En kafé, takeaway-virksomhet, hotellrestaurant og pizzeria
              har ulike arbeidsflyter.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Vanlige bestillingstyper</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    <strong>Takeaway-bestilling:</strong> Gjesten bestiller
                    online og henter senere.
                  </li>

                  <li className="text__seo-li">
                    <strong>Leveringsbestilling:</strong> Gjesten bestiller
                    online og får levering.
                  </li>

                  <li className="text__seo-li">
                    <strong>Forhåndsbestilling:</strong> Gjesten velger tidspunkt
                    og produkter før besøket.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Fleksible første steg</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    <strong>QR-kode-bestilling:</strong> Gjesten skanner koden og
                    bestiller fra bordet.
                  </li>

                  <li className="text__seo-li">
                    <strong>Strukturert forespørsel:</strong> Restauranten
                    mottar en bestillingsforespørsel i stedet for full
                    automatisering med en gang.
                  </li>

                  <li className="text__seo-li">
                    <strong>Meny med bestillingsmulighet:</strong> Den digitale
                    menyen blir inngangspunktet.
                  </li>
                </ul>
              </div>
            </div>

            <p>
              For mange restauranter er det beste første steget ikke “alt på én
              gang”. Det er et tydelig grunnlag: digital meny, QR-kode,
              forståelige produkter og senere en bestillingsflyt.
            </p>
          </div>
        </section>

        <section id="expectation" className="acquaintance">
          <h2>Hvorfor digital bestilling blir normalt for gjester</h2>

          <p>
            Gjester er vant til å gjøre ting på smarttelefonen: se menyer,
            reservere bord, bestille eller betale. Det betyr ikke at alle
            restauranter må automatisere alt. Det betyr at en enkel digital vei
            blir mer og mer forventet.
          </p>

          <p>
            For Best Place er den viktige ideen denne: digital bestilling bør
            ikke være isolert. Den bør kobles til stedet der gjesten allerede er
            — menyen, QR-koden, restaurantsiden eller reservasjonsflyten.
          </p>

          <h3>Digital bestilling bør føles som neste steg</h3>

          <p>
            Først vil gjesten forstå tilbudet. Deretter, hvis neste steg er
            tydelig, kan gjesten sende en bestilling, be om henting, reservere
            bord eller be om mer informasjon.
          </p>

          <VisualIdea>
            En gjest åpner en digital meny på smarttelefonen, sjekker retter og
            priser, og går naturlig videre til en enkel bestillings- eller
            forespørselsknapp.
          </VisualIdea>
        </section>

        <section id="direct" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>
              Direkte bestilling eller tredjepartsplattformer: hva er bedre?
            </h2>

            <p>
              Tredjepartsplattformer kan gi rekkevidde og være nyttige for mange
              restauranter. Samtidig er det verdifullt for restauranter å bygge
              sin egen digitale vei også.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Tredjepartsplattform</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Kan gi ekstra rekkevidde.
                  </li>

                  <li className="text__seo-li">
                    Gjester kjenner kanskje allerede plattformen.
                  </li>

                  <li className="text__seo-li">
                    Restauranten er avhengig av plattformens regler og layout.
                  </li>

                  <li className="text__seo-li">
                    Gjesteforholdet tilhører ikke alltid restauranten fullt ut.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Egen digital bestillingsvei</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Mer kontroll over meny og presentasjon.
                  </li>

                  <li className="text__seo-li">
                    Direkte lenke fra nettside eller QR-kode.
                  </li>

                  <li className="text__seo-li">
                    Bedre kobling til restaurantens merkevare.
                  </li>

                  <li className="text__seo-li">
                    Kan utvides steg for steg.
                  </li>
                </ul>
              </div>
            </div>

            <p>
              Den beste strategien trenger ikke være enten-eller. En restaurant
              kan bruke plattformer og samtidig bygge sin egen digitale struktur.
            </p>
          </div>
        </section>

        <section id="qr-ordering" className="acquaintance">
          <h2>QR-kode-bestilling: bestilling direkte fra bordet</h2>

          <p>
            QR-kode-bestilling betyr at gjester skanner en QR-kode ved bordet,
            åpner den digitale menyen og starter en bestilling eller forespørsel
            derfra.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">Skann QR-kode.</li>
            <li className="text__seo-li">Åpne menyen.</li>
            <li className="text__seo-li">Velg produkter.</li>
            <li className="text__seo-li">Send bestilling.</li>
            <li className="text__seo-li">Restauranten behandler den.</li>
          </ol>

          <p>
            Dette kan være spesielt interessant for konsepter der gjester ofte
            vil bestille mer: drikke, desserter, snacks, menyer eller en ny
            runde.
          </p>

          <div className="seoCompareSmall">
            <div>
              <strong>Relatert side</strong>
              <p>
                <Link to="/no/qr-kode-meny-for-restauranter">
                  QR-kode-meny
                </Link>
              </p>
            </div>

            <div>
              <strong>Relatert side</strong>
              <p>
                <Link to="/no/digital-meny-for-restauranter">
                  Digital meny
                </Link>
              </p>
            </div>
          </div>
        </section>

        <section id="workflow" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvordan en god digital bestillingsflyt bør se ut</h2>

            <p>
              Et godt bestillingssystem er ikke bare en knapp. Det er en tydelig
              arbeidsflyt som reduserer feil og gir gjesten trygghet.
            </p>

            <ol className="seoSteps">
              <li className="text__seo-li">
                Gjesten åpner menyen via nettside eller QR-kode.
              </li>

              <li className="text__seo-li">
                Gjesten ser kategorier, priser, bilder og tilgjengelighet.
              </li>

              <li className="text__seo-li">Gjesten velger produkter.</li>

              <li className="text__seo-li">
                Gjesten ser sammendrag og totalpris.
              </li>

              <li className="text__seo-li">
                Gjesten velger henting, bordbestilling eller forespørsel.
              </li>

              <li className="text__seo-li">
                Restauranten mottar tydelige bestillingsdata.
              </li>

              <li className="text__seo-li">
                Teamet bekrefter eller behandler bestillingen.
              </li>
            </ol>

            <p>
              Jo tydeligere denne flyten er, desto færre spørsmål oppstår.
              Digital bestilling bør ikke være mer komplisert enn en telefonsamtale
              — den bør være tydeligere.
            </p>
          </div>
        </section>

        <section id="payment" className="acquaintance">
          <h2>Digital betaling og kontaktløse arbeidsflyter</h2>

          <p>
            Ikke alle restauranter trenger digital betaling med en gang. Men
            bestilling, henting og betaling hører sammen på lengre sikt. Hvis en
            gjest bestiller digitalt, forventer han eller hun ofte et tydelig
            neste steg for betaling eller henting.
          </p>

          <p>
            For en første versjon kan restauranten starte med strukturerte
            forespørsler eller forhåndsbestillinger. Senere kan online betaling,
            QR-betaling, betaling ved henting eller kontaktløs betaling legges
            til avhengig av restaurantkonseptet.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">
              Strukturert forespørsel uten betaling.
            </li>

            <li className="text__seo-li">
              Hentebestilling med betaling på stedet.
            </li>

            <li className="text__seo-li">
              Forhåndsbestilling med senere bekreftelse.
            </li>

            <li className="text__seo-li">
              Digital betaling som senere steg.
            </li>

            <li className="text__seo-li">
              Kontaktløs betaling for en smidigere gjesteflyt.
            </li>
          </ul>

          <h3>Betaling trenger ikke være første steg</h3>

          <p>
            En restaurant kan først teste om gjestene forstår den digitale
            menyen, produktvalget og bestillingsflyten. Betaling kan legges til
            når grunnprosessen er tydelig.
          </p>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Vanlige feil i digitale bestillingssystemer</h2>

            <p>
              Digital bestilling hjelper bare når flyten er tydelig for gjester
              og håndterbar for teamet. Hvis restauranten starter med for mye
              kompleksitet, kan systemet skape nye problemer i stedet for å løse
              gamle.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Vanlige feil</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Bestilling legges til før menyen er tydelig.
                  </li>

                  <li className="text__seo-li">
                    Det finnes for mange unødvendige steg.
                  </li>

                  <li className="text__seo-li">
                    Utsolgte produkter forblir tilgjengelige.
                  </li>

                  <li className="text__seo-li">
                    Detaljer om henting eller levering er uklare.
                  </li>

                  <li className="text__seo-li">
                    Gjesten får ingen tydelig bekreftelse.
                  </li>

                  <li className="text__seo-li">
                    Teamet er ikke inkludert i arbeidsflyten.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Bedre</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Bygg grunnlaget med digital meny først.
                  </li>

                  <li className="text__seo-li">
                    Hold bestillingsflyten kort.
                  </li>

                  <li className="text__seo-li">
                    Skjul utilgjengelige produkter med en gang.
                  </li>

                  <li className="text__seo-li">
                    Forklar henting, bordbestilling eller forespørsel tydelig.
                  </li>

                  <li className="text__seo-li">
                    Vis tydelig bekreftelse etter sending.
                  </li>

                  <li className="text__seo-li">
                    Lær teamet hvordan forespørsler behandles.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="no" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>Vanlige spørsmål om digitale bestillingssystemer</h2>

          <div className="seoFaq">
            <details>
              <summary>
                Trenger alle restauranter et digitalt bestillingssystem?
              </summary>

              <p>
                Nei. Hvis du ikke tilbyr henting, levering eller bordbestilling,
                kan en digital meny være det viktigste første steget.
              </p>
            </details>

            <details>
              <summary>Er digital bestilling bare for levering?</summary>

              <p>
                Nei. Det kan også brukes til henting, forhåndsbestilling eller
                bordbestilling.
              </p>
            </details>

            <details>
              <summary>Kan QR-kode-bestilling erstatte service?</summary>

              <p>
                Ikke nødvendigvis. I mange restauranter støtter det service, for
                eksempel ved ekstra drikke, desserter eller gjentatte
                bestillinger.
              </p>
            </details>

            <details>
              <summary>
                Er direkte bestilling bedre enn å bruke en plattform?
              </summary>

              <p>
                Ikke alltid. Plattformer kan gi rekkevidde. En direkte digital
                vei gir mer kontroll over merkevare, meny og gjesteforhold.
              </p>
            </details>

            <details>
              <summary>Må betaling integreres med en gang?</summary>

              <p>
                Nei. Noen restauranter starter med strukturerte forespørsler
                eller forhåndsbestillinger og legger til digital betaling senere.
              </p>
            </details>

            <details>
              <summary>Hva bør implementeres først?</summary>

              <p>
                En tydelig digital meny er grunnlaget. Uten god menystruktur blir
                også bestilling vanskeligere.
              </p>
            </details>
          </div>
        </section>

        <SeoRelatedLinks
          lang="no"
          group="business"
          exclude="/no/digitalt-bestillingssystem-for-restauranter"
        />
      </article>
    </main>
  );
}